import { useState } from "react";
import { Alert, Badge, Button, Table } from "flowbite-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { HiInformationCircle, HiSpeakerphone } from "react-icons/hi";
import { IoMdSearch } from "react-icons/io";
import { MdAdd, MdOutlineDrafts, MdOutlineMarkEmailRead } from "react-icons/md";
import { FiEye } from "react-icons/fi";

import { ANNOUNCEMENTS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import Modal from "../modals/Modal";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import {
  formatDateTime,
  formatNumber,
  getErrorMessage,
} from "../customers/customerHelpers";
import { SkeletonWorkList } from "../crm/Skeletons";

const MAX_TITLE = 80;
const MAX_MESSAGE = 500;

const fieldClass =
  "block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0";

/** Barra de leitura: o número sozinho não diz se 231 é muito ou pouco. */
const ReadBar = ({ reads, audience, rate }) => (
  <div className="min-w-[130px]">
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-sm font-semibold text-slate-900">
        {formatNumber(reads)}
        <span className="text-xs font-normal text-slate-500">
          {" "}
          / {formatNumber(audience)}
        </span>
      </span>
      <span className="text-xs font-medium text-slate-600">{rate}%</span>
    </div>
    <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100">
      <div
        className="h-1.5 rounded-full bg-pink-500"
        style={{ width: `${Math.min(rate, 100)}%` }}
      />
    </div>
  </div>
);

const PersonRow = ({ person }) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
    <div className="min-w-0">
      <p className="truncate text-sm font-medium text-slate-900">
        {person.name}
      </p>
      <p className="truncate text-xs text-slate-500">
        {person.companyName} · {person.email}
      </p>
    </div>
    {person.readAt ? (
      <span className="shrink-0 text-xs text-slate-500">
        {formatDateTime(person.readAt)}
      </span>
    ) : null}
  </div>
);

/** Painel de leituras de um comunicado. */
const AudiencePanel = ({ announcementId }) => {
  const [tab, setTab] = useState("readers");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 400);

  const { isLoading, data } = useQuery(
    ["announcement-audience", announcementId, debouncedSearch],
    async () => {
      const { data: response } = await axios.get(
        `${ANNOUNCEMENTS.LIST}/${announcementId}/audience?search=${encodeURIComponent(
          debouncedSearch
        )}`
      );

      return response.data;
    },
    { enabled: Boolean(announcementId), keepPreviousData: true }
  );

  const announcement = data?.announcement;
  const counts = data?.counts || {};
  const list = tab === "readers" ? data?.readers || [] : data?.pending || [];

  return (
    <div className="space-y-4">
      {announcement ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="whitespace-pre-line text-sm text-slate-700">
            {announcement.message}
          </p>
          <p className="mt-3 border-t border-slate-100 pt-2 text-xs text-slate-500">
            Enviado em {formatDateTime(announcement.createdAt)}
          </p>
        </section>
      ) : null}

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[
          {
            label: "Leram",
            value: counts.totalReads,
            className: "text-emerald-600",
          },
          {
            label: "Ainda não leram",
            value: counts.pending,
            className: "text-amber-600",
          },
          {
            label: "Taxa de leitura",
            value: `${announcement?.readRate ?? 0}%`,
            className: "text-slate-900",
          },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-2xl border border-slate-200 bg-white p-4"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              {card.label}
            </p>
            <p className={`text-2xl font-bold ${card.className}`}>
              {typeof card.value === "number"
                ? formatNumber(card.value)
                : card.value}
            </p>
          </div>
        ))}
      </section>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          {[
            {
              key: "readers",
              label: `Leram (${formatNumber(counts.readers)})`,
              icon: MdOutlineMarkEmailRead,
            },
            {
              key: "pending",
              label: `Não leram (${formatNumber(counts.pending)})`,
              icon: MdOutlineDrafts,
            },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                tab === item.key
                  ? "border-pink-500 bg-pink-50 text-pink-600"
                  : "border-slate-200 bg-white text-slate-500 hover:text-slate-800"
              }`}
            >
              <item.icon size={14} />
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 focus-within:border-pink-500">
          <IoMdSearch className="shrink-0 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Buscar por nome, e-mail ou empresa"
            className="w-full border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        // Esqueleto com a forma da lista: o spinner centralizado não dizia
        // quantas linhas vinham nem reservava a altura delas.
        <SkeletonWorkList rows={5} withHeader={false} />
      ) : list.length ? (
        <div className="space-y-2">
          {list.map((person) => (
            <PersonRow key={person.id} person={person} />
          ))}
        </div>
      ) : (
        <p className="py-10 text-center text-sm text-slate-500">
          {searchTerm
            ? "Ninguém encontrado com esse termo."
            : tab === "readers"
              ? "Ninguém leu este comunicado ainda."
              : "Todos os usuários já leram."}
        </p>
      )}

      {counts.totalReads > counts.readers ? (
        <p className="text-xs text-slate-500">
          {formatNumber(counts.totalReads - counts.readers)} leitura(s) vieram de
          usuários que já saíram da base — contam no total, mas não aparecem na
          lista.
        </p>
      ) : null}
    </div>
  );
};

/**
 * Comunicados enviados e o desempenho de leitura de cada um.
 * A base de comparação é o número de usuários ativos hoje.
 */
const AnnouncementsTable = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [feedback, setFeedback] = useState(null);

  const debouncedSearch = useDebouncedValue(searchTerm, 400);

  const { isLoading, data } = useQuery(
    ["announcements", debouncedSearch],
    async () => {
      const { data: response } = await axios.get(
        `${ANNOUNCEMENTS.LIST}?search=${encodeURIComponent(debouncedSearch)}`
      );

      return response.data;
    }
  );

  const { data: recipients } = useQuery(["announcement-recipients"], async () => {
    const { data: response } = await axios.get(ANNOUNCEMENTS.RECIPIENTS);

    return response.data;
  });

  const announcements = data?.data || [];
  const summary = data?.summary || {};

  const sendMutation = useMutation(
    (payload) => axios.post(ANNOUNCEMENTS.BROADCAST, payload),
    {
      onSuccess: ({ data: response }) => {
        const push = response?.data?.push;
        setFeedback({
          type: "success",
          message: `Comunicado enviado para ${formatNumber(
            response?.data?.recipients ?? 0
          )} usuário(s).${
            push ? ` Push: ${formatNumber(push.successCount)} entregues.` : ""
          }`,
        });
        setTitle("");
        setMessage("");
        setIsComposerOpen(false);
        queryClient.invalidateQueries("announcements");
      },
      onError: (error) =>
        setFeedback({ type: "failure", message: getErrorMessage(error) }),
    }
  );

  const toggleMutation = useMutation(
    ({ id, isActive }) =>
      axios.put(`${ANNOUNCEMENTS.LIST}/${id}`, { isActive }),
    {
      onSuccess: () => queryClient.invalidateQueries("announcements"),
      onError: (error) =>
        setFeedback({ type: "failure", message: getErrorMessage(error) }),
    }
  );

  const canSubmit =
    title.trim() && message.trim() && !sendMutation.isLoading;

  const handleSend = (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    const total = recipients?.total ?? 0;

    if (
      !window.confirm(
        `Enviar este comunicado para ${formatNumber(total)} usuário(s) do sistema?`
      )
    ) {
      return;
    }

    sendMutation.mutate({ title: title.trim(), message: message.trim() });
  };

  return (
    <>
      <div className="space-y-4">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Comunicação
              </p>
              <h2 className="text-xl font-semibold text-slate-900">
                Comunicados
              </h2>
              <p className="text-sm text-slate-500">
                Avisos enviados a todos os usuários e quem já leu cada um.
              </p>
            </div>
            <Button size="sm" onClick={() => setIsComposerOpen(true)}>
              <MdAdd className="mr-2 h-4 w-4" />
              Novo comunicado
            </Button>
          </div>
        </section>

        {feedback ? (
          <Alert
            color={feedback.type}
            icon={HiInformationCircle}
            onDismiss={() => setFeedback(null)}
          >
            {feedback.message}
          </Alert>
        ) : null}

        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label: "Comunicados", value: summary.total },
            { label: "Ativos", value: summary.active },
            { label: "Leituras totais", value: summary.totalReads },
            {
              label: "Taxa média de leitura",
              value: `${summary.averageReadRate ?? 0}%`,
            },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                {card.label}
              </p>
              <p className="text-2xl font-bold text-slate-900">
                {typeof card.value === "number"
                  ? formatNumber(card.value)
                  : card.value}
              </p>
            </div>
          ))}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 focus-within:border-pink-500">
            <IoMdSearch className="shrink-0 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por título ou conteúdo"
              className="w-full border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </section>

        <section className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <Table hoverable>
            <Table.Head className="bg-slate-50">
              <Table.HeadCell>Comunicado</Table.HeadCell>
              <Table.HeadCell className="hidden md:table-cell">
                Enviado em
              </Table.HeadCell>
              <Table.HeadCell>Leitura</Table.HeadCell>
              <Table.HeadCell className="hidden lg:table-cell">
                Última leitura
              </Table.HeadCell>
              <Table.HeadCell>Status</Table.HeadCell>
              <Table.HeadCell className="text-center">Ações</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {isLoading ? (
                Array.from({ length: 3 }).map((_, rowIndex) => (
                  <Table.Row key={rowIndex}>
                    {Array.from({ length: 6 }).map((__, cellIndex) => (
                      <Table.Cell key={cellIndex}>
                        <div className="h-3 w-full animate-pulse rounded-full bg-slate-200" />
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))
              ) : announcements.length ? (
                announcements.map((announcement) => (
                  <Table.Row key={announcement.id} className="bg-white">
                    <Table.Cell className="max-w-[320px]">
                      <button
                        type="button"
                        onClick={() => setSelected(announcement)}
                        className="truncate text-left text-sm font-semibold text-slate-900 hover:text-pink-600"
                      >
                        {announcement.title}
                      </button>
                      <p className="truncate text-xs text-slate-500">
                        {announcement.message}
                      </p>
                    </Table.Cell>
                    <Table.Cell className="hidden whitespace-nowrap text-xs text-slate-600 md:table-cell">
                      {formatDateTime(announcement.createdAt)}
                    </Table.Cell>
                    <Table.Cell>
                      <ReadBar
                        reads={announcement.reads}
                        audience={announcement.audience}
                        rate={announcement.readRate}
                      />
                    </Table.Cell>
                    <Table.Cell className="hidden whitespace-nowrap text-xs text-slate-600 lg:table-cell">
                      {announcement.lastReadAt
                        ? formatDateTime(announcement.lastReadAt)
                        : "—"}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        color={announcement.isActive ? "success" : "gray"}
                        className="w-fit text-xs"
                      >
                        {announcement.isActive ? "Ativo" : "Arquivado"}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="xs"
                          color="light"
                          onClick={() => setSelected(announcement)}
                          title="Ver quem leu"
                        >
                          <FiEye />
                        </Button>
                        <Button
                          size="xs"
                          color="light"
                          disabled={toggleMutation.isLoading}
                          onClick={() =>
                            toggleMutation.mutate({
                              id: announcement.id,
                              isActive: !announcement.isActive,
                            })
                          }
                          title={
                            announcement.isActive
                              ? "Arquivar (some do app)"
                              : "Reativar"
                          }
                        >
                          {announcement.isActive ? "Arquivar" : "Reativar"}
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell
                    colSpan={6}
                    className="py-10 text-center text-sm text-slate-500"
                  >
                    Nenhum comunicado encontrado.
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </section>
      </div>

      <Modal
        isOpen={Boolean(selected)}
        onClose={() => setSelected(null)}
        size="lg"
        icon={HiSpeakerphone}
        title={selected?.title || "Comunicado"}
        subtitle={
          selected
            ? `${formatNumber(selected.reads)} leitura(s) · ${selected.readRate}% da base`
            : undefined
        }
        bodyClassName="bg-slate-50/50"
      >
        {selected ? <AudiencePanel announcementId={selected.id} /> : null}
      </Modal>

      <Modal
        isOpen={isComposerOpen}
        onClose={setIsComposerOpen}
        size="md"
        icon={HiSpeakerphone}
        title="Novo comunicado"
        subtitle={
          recipients
            ? `${formatNumber(recipients.total)} usuários ativos · ${formatNumber(
                recipients.withPush
              )} com push`
            : "Calculando destinatários…"
        }
        closeOnBackdrop={false}
      >
        <form className="space-y-4" onSubmit={handleSend}>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label
                className="text-xs font-medium text-slate-600"
                htmlFor="announcement-title"
              >
                Título
              </label>
              <span className="text-xs text-slate-400">
                {title.length}/{MAX_TITLE}
              </span>
            </div>
            <input
              id="announcement-title"
              type="text"
              className={fieldClass}
              placeholder="Ex.: Nova funcionalidade disponível"
              value={title}
              maxLength={MAX_TITLE}
              onChange={(event) => setTitle(event.target.value)}
              required
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label
                className="text-xs font-medium text-slate-600"
                htmlFor="announcement-message"
              >
                Mensagem
              </label>
              <span className="text-xs text-slate-400">
                {message.length}/{MAX_MESSAGE}
              </span>
            </div>
            <textarea
              id="announcement-message"
              rows={6}
              className={`${fieldClass} resize-y`}
              placeholder="Escreva o comunicado que os usuários verão no app…"
              value={message}
              maxLength={MAX_MESSAGE}
              onChange={(event) => setMessage(event.target.value)}
              required
            />
          </div>

          <div className="flex gap-2">
            <Button
              type="submit"
              className="flex-1"
              disabled={!canSubmit}
              isProcessing={sendMutation.isLoading}
            >
              Enviar comunicado
            </Button>
            <Button color="light" onClick={() => setIsComposerOpen(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default AnnouncementsTable;
